// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

/* eslint-disable spire/fenced-imports */
import Tippy, { TippyProps } from "@tippyjs/react";
import { hideAll, Instance as TippyInstance, sticky } from "tippy.js";
/* eslint-enable spire/fenced-imports */

import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Icon from "@insite/mobius/Icon";
import * as ariaAppHider from "@insite/mobius/Overlay/helpers/ariaAppHider";
import { injectCss } from "@insite/mobius/utilities";
import { TippyPopupStyle, tippyPopupStyles } from "@sc/App/ComponentExtensions/Tippy/TippyPopupExtensions";
import { importTippyCssFiles } from "@sc/App/Components/Tippy/tippy.helper";
import { useTippyProps } from "@sc/App/Components/Tippy/tippy.hooks";
import { xHeaderUpdate } from "@sc/App/Handlers/XNG/Header";
import { CustomAppState } from "@sc/App/Store/Reducers";
import { addScrollbarGutter, removeScrollbarGutter, xClassNames } from "@sc/XDL/Utilities";
import { mergeWithCss } from "@sc/XDL/Utilities/mergeWithCss";
import React, { FC, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import styled from "styled-components";

export const XngTippy = styled(Tippy)<TippyProps>`
    ${injectCss}
`;

export interface TippyPopupProps extends TippyProps {
    /**
     * Show or hide close button.
     */
    showCloseButton?: "both" | "mobile" | "desktop" | false;
    /**
     * Show tooltip as menu like MiniCart or as tooltip.
     */
    type?: "menu" | "tooltip";
    /**
     * Show tooltip as left-side or default on mobile.
     */
    mobileMode?: "left-side" | "default";
    /**
     * Event will be fired when popup open and close.
     */
    onTriggerPopupOpen?: (isOpen: boolean) => void;
    /**
     * To remove span tag with element wrapper.
     */
    removeWrapper?: boolean;
    extendedStyles?: TippyPopupStyle;
}

type Props = Omit<TippyPopupProps, "children"> & {
    children?: React.ReactElement<any> | ((expanded: boolean) => React.ReactElement<any>);
};

const TippyPopup: FC<Props> = props => {
    const {
        type,
        onTriggerPopupOpen: onTriggerPopupOpenProps,
        showCloseButton,
        extendedStyles,
        children,
        removeWrapper,
        content: tippyContent,
        ...otherProps
    } = props;

    const dispatch = useDispatch();
    const [styles] = useState(() => mergeWithCss(tippyPopupStyles, extendedStyles));
    const [instance, setInstance] = useState<TippyInstance>();
    const [expanded, setExpanded] = useState(false);
    const options = useTippyProps(otherProps);
    const headerHeight = useAppSelector(state => (state as CustomAppState)?.xHeader?.headerHeight);

    useEffect(() => {
        importTippyCssFiles();
    }, []);

    useEffect(() => {
        if (instance && otherProps.mobileMode === "left-side") {
            if (instance.popper.style.getPropertyValue("--tippy-top-offset") !== headerHeight) {
                instance.popper.className = xClassNames.append("tippy-menu-left-side", instance.popper.className);
                instance.popper.style.setProperty("--tippy-top-offset", `${headerHeight || "156px"}`);
            }
        }
    }, [headerHeight, instance]);

    const closeButtonHandler = () => {
        instance?.hide?.();
    };

    const onCreateHandler = (instance: TippyInstance) => {
        setInstance(instance);
        otherProps.onCreate?.(instance);
    };

    const onShowHandler = (instance: TippyInstance) => {
        /**
         * Close all Tippy instances
         */
        hideAll();

        /**
         * Show overlay menu
         */
        dispatch(xHeaderUpdate({ key: "registerMenu", value: `${instance.id}_popup` }));

        /**
         * Add overflow: hidden for body and add scrollbar-gutter for html.
         * we need it to prevent page scroll when tippy opened
         */
        addScrollbarGutter();
        ariaAppHider.hide(document.body);
        document.querySelectorAll("select").forEach(select => select.blur());
        otherProps.onShow?.(instance);
        onTriggerPopupOpenProps?.(true);
    };

    const onShownHandler = (instance: TippyInstance) => {
        if (type === "menu") {
            instance.popper.className = xClassNames.append("menu-opened", instance.popper.className);
        }

        otherProps.onShown?.(instance);
        setExpanded(true);
    };

    const onHideHandler = (instance: TippyInstance) => {
        if (type === "menu") {
            instance.popper.className = xClassNames.remove("menu-opened", instance.popper.className);
        }

        /**
         * Hide overlay menu
         */
        dispatch(xHeaderUpdate({ key: "unregisterMenu", value: `${instance.id}_popup` }));

        /**
         * Remove overflow: hidden from body and add scrollbar-gutter from html.
         * we need it to make page scrollable after tippy closed
         */
        removeScrollbarGutter();
        ariaAppHider.show(document.body);

        otherProps.onHide?.(instance);
        onTriggerPopupOpenProps?.(false);
        setExpanded(false);
    };

    const renderedChildren = typeof children === "function" ? (children as any)(expanded) : children;
    return (
        <StyledWrapper {...styles.main}>
            <XngTippy
                {...options}
                onCreate={onCreateHandler}
                onShow={onShowHandler}
                onShown={onShownHandler}
                onHide={onHideHandler}
                content={
                    <StyledWrapper className="content-wrapper">
                        <Icon
                            {...styles.closeButton}
                            className={`tippy-close-icon ${showCloseButton || ""}`}
                            onClick={closeButtonHandler}
                            aria-label="Close Button"
                        />
                        {tippyContent}
                    </StyledWrapper>
                }
                visible={otherProps.visible}
                {...styles.main}
            >
                {removeWrapper ? renderedChildren : <span style={styles.elementWrapper}>{renderedChildren}</span>}
            </XngTippy>
        </StyledWrapper>
    );
};

TippyPopup.defaultProps = {
    showCloseButton: false,
    mobileMode: "default",
    type: "tooltip",
};

export default TippyPopup;
